import React, { useCallback, useEffect, useState } from 'react'
import { Box, Button, Card, Table, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import UploadFileOutlinedIcon from '@mui/icons-material/UploadFileOutlined'
import { useNavigate } from 'react-router-dom'
import EmptyState from '../merchants/shared/EmptyState'
import { C, SX, TYPE } from '../merchants/shared/tokens'
import { errorText, fetchMerchants, fetchTatCouriers, fetchTatGrid } from './ptlSetupApi'
import { ROUTES, fmtDate, num, tatTabs } from './constants'
import { Banner } from './components/SetupUi'
import PtlPage from './shell/PtlPage'

const head = (labels) => (
  <TableHead>
    <TableRow>
      {labels.map((h, i) => (
        <TableCell key={i} sx={{ ...TYPE.eyebrow, fontSize: 10, color: C.inkFaint, bgcolor: C.surfaceAlt, borderColor: C.line, py: 1.25, whiteSpace: 'nowrap' }}>{h}</TableCell>
      ))}
    </TableRow>
  </TableHead>
)

/**
 * PTL FREIGHT → PTL Merchant Setup → Merchant TAT → Courier TAT. Each PTL courier account's own TAT (Prozo level,
 * GET tat/couriers): lanes it gives, merchants on it, last change. A merchant is put on a courier's TAT in onboarding
 * Step 2; lanes the courier has not given follow the standard, and the merchant's custom lanes still win. A row opens
 * the courier's lanes (PtlTatCourierLanes).
 */
export default function PtlTatCourier() {
  const navigate = useNavigate()
  const [couriers, setCouriers] = useState(null)
  const [stdLanes, setStdLanes] = useState(null)
  const [onCustom, setOnCustom] = useState(undefined)
  const [error, setError] = useState('')

  const load = useCallback(() => {
    setError('')
    fetchTatCouriers().then(setCouriers).catch((e) => setError(errorText(e, 'Could not load the couriers')))
    fetchTatGrid(null).then((g) => setStdLanes(g.cells.length)).catch(() => setStdLanes(0))
    fetchMerchants().then((list) => setOnCustom(list.filter((m) => m.customTatLanes > 0).length)).catch(() => setOnCustom(undefined))
  }, [])
  useEffect(() => { load() }, [load])

  const live = (couriers || []).filter((c) => !c.notLive)
  const withTat = (couriers || []).filter((c) => c.lanes > 0)
  const merchantsOn = withTat.reduce((n, c) => n + c.merchants, 0)
  const lastChange = withTat.map((c) => c.latest).filter(Boolean).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))[0]
  const kpis = [
    { key: 'live', label: 'PTL courier accounts', value: couriers ? num(live.length) : '', sub: 'Live B2B accounts' },
    { key: 'with', label: 'With their own TAT', value: couriers ? num(withTat.length) : '', sub: 'Uploaded here' },
    { key: 'merchants', label: 'Merchants on a courier TAT', value: couriers ? num(merchantsOn) : '', sub: 'The rest are on the standard' },
    { key: 'last', label: 'Last change', value: lastChange ? fmtDate(lastChange.createdAt).split(',')[0] : 'None',
      sub: lastChange ? `${lastChange.courier || ''}${lastChange.createdBy ? ` · ${lastChange.createdBy}` : ''}` : 'No courier TAT yet' },
  ]
  const noStandard = stdLanes === 0

  return (
    <PtlPage title='Merchant TAT' tabTitle='Courier TAT' tabs={tatTabs(onCustom, couriers ? withTat.length : undefined)}
      description='Each courier’s own TAT by lane, kept apart from the Prozo standard. A merchant that ships with one courier only can be put on that courier’s TAT in onboarding Step 2; lanes the courier has not given follow the standard.'
      kpis={kpis} kpisLoading={!couriers && !error}>
      {error && <Banner tone='danger' action={<Button size='small' onClick={load} sx={{ textTransform: 'none' }}>Retry</Button>}>{error}</Banner>}
      {noStandard && (
        <Banner tone='warn' action={<Button size='small' onClick={() => navigate(ROUTES.tat)} sx={{ textTransform: 'none' }}>Open Standard TAT</Button>}>
          Upload the Prozo standard TAT first. A courier TAT uses its lanes and zones.
        </Banner>
      )}
      {couriers && couriers.length === 0 ? (
        <EmptyState title='No PTL courier account' body='Courier TAT is kept per live PTL (B2B) courier account. None is live yet.' />
      ) : couriers && (
        <Card elevation={0} sx={{ ...SX.card, overflowX: 'auto' }}>
          <Table size='small' sx={{ minWidth: 680 }} aria-label='Courier TAT'>
            {head(['Courier', 'TAT', 'Merchants on it', 'Last change', 'Action'])}
            <TableBody>
              {couriers.map((c) => {
                const has = c.lanes > 0
                const open = () => navigate(has ? ROUTES.tatCourier(c.courier) : ROUTES.tatCourierUpload(c.courier))
                return (
                  <TableRow key={c.courier} hover={!c.notLive} onClick={c.notLive && !has ? undefined : open}
                    sx={{ cursor: c.notLive && !has ? 'default' : 'pointer', '& td': { borderColor: C.lineSoft, py: 1.1 } }}>
                    <TableCell>
                      <Typography sx={TYPE.rowLabel}>{c.courier}</Typography>
                      <Typography sx={{ ...TYPE.meta, color: C.inkFaint }}>{c.notLive ? 'Not a live account any more' : c.accountCode}</Typography>
                    </TableCell>
                    <TableCell>
                      {has ? (
                        <Box component='span' sx={{ ...SX.pill, color: C.ok, bgcolor: C.okSoft, border: `1px solid ${C.okBorder}` }}>
                          {num(c.lanes)}{stdLanes ? ` of ${num(stdLanes)}` : ''} lanes
                        </Box>
                      ) : (
                        <Typography component='span' sx={{ ...TYPE.rowHelp }}>Not uploaded</Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ ...TYPE.value, fontVariantNumeric: 'tabular-nums', color: c.merchants ? C.ink : C.inkMuted }}>{num(c.merchants)}</TableCell>
                    <TableCell sx={{ fontSize: 12.5, color: C.inkMuted, whiteSpace: 'nowrap' }}>{c.latest ? fmtDate(c.latest.createdAt) : '—'}</TableCell>
                    <TableCell align='right'>
                      {has ? (
                        <Button size='small' variant='outlined' endIcon={<ChevronRightIcon />} onClick={(e) => { e.stopPropagation(); open() }}
                          sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32 }}>Manage</Button>
                      ) : !c.notLive && (
                        <Button size='small' variant='outlined' startIcon={<UploadFileOutlinedIcon />} disabled={noStandard} onClick={(e) => { e.stopPropagation(); open() }}
                          sx={{ ...SX.ctaGhost, fontSize: 12.5, minHeight: 32, whiteSpace: 'nowrap' }}>Upload TAT</Button>
                      )}
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        </Card>
      )}
      <Typography sx={{ ...TYPE.rowHelp, mt: 1.5, color: C.inkMuted }}>
        A change applies to shipments priced after it is saved, for every merchant on that courier’s TAT.
      </Typography>
    </PtlPage>
  )
}
